import { Mail, MapPin, Mountain, Phone } from "lucide-react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useLang } from "@/i18n";
import Reveal from "@/components/Reveal";

const POSITION = [45.273386, 6.056759];
const EMAIL = "contact@restobreda.com";

const pinIcon = L.divIcon({
  className: "restobreda-pin",
  html: '<div style="width:20px;height:20px;border-radius:50%;background:#1A362D;border:4px solid #FAFAFA;box-shadow:0 3px 12px rgba(0,0,0,.4)"></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

export default function Contact() {
  const { t } = useLang();
  const c = t.contact;

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 bg-pine py-24 text-paper md:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-10 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-wood-light">{c.eyebrow}</p>
            <h2 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight md:text-5xl">
              {c.title}
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-paper/75 md:text-lg">{c.text}</p>
          </Reveal>

          <Reveal delay={0.15}>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email"
              className="group mt-10 inline-flex items-center gap-4 border-b border-paper/25 pb-3 transition-colors duration-300 hover:border-wood-light"
            >
              <Mail size={22} className="text-wood-light" />
              <span className="font-serif text-2xl tracking-tight transition-colors duration-300 group-hover:text-wood-light md:text-3xl">
                {EMAIL}
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <Phone size={20} className="mt-1 shrink-0 text-wood-light" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper/50">
                    {c.phoneLabel}
                  </p>
                  <a
                    href="tel:+33642424207"
                    data-testid="contact-phone"
                    className="mt-1 block text-base text-paper/85 transition-colors duration-300 hover:text-wood-light"
                  >
                    +33 (0)6 42 42 42 07
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="mt-1 shrink-0 text-wood-light" />
                <div data-testid="contact-address">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-paper/50">
                    {c.addressLabel}
                  </p>
                  {c.addressLines.map((line, i) => (
                    <p key={i} className="mt-1 text-base text-paper/85">
                      {line}
                    </p>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Mountain size={20} className="shrink-0 text-wood-light" />
                <p className="text-sm uppercase tracking-[0.14em] text-paper/60">{c.area}</p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div
            data-testid="contact-map"
            className="h-[420px] overflow-hidden rounded-2xl border border-paper/15 shadow-2xl md:h-[500px]"
          >
            <MapContainer
              center={POSITION}
              zoom={13}
              scrollWheelZoom={false}
              className="h-full w-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={POSITION} icon={pinIcon}>
                <Popup>{c.mapPopup}</Popup>
              </Marker>
            </MapContainer>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
